import React, { useState } from 'react';
import { Percent } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const classify = (sex, bf, t) => {
  if (sex === 'homme') {
    if (bf < 6) return { label: t('bf_cat_essential'), color: '#3B82F6' };
    if (bf < 14) return { label: t('bf_cat_athletic'), color: '#10B981' };
    if (bf < 18) return { label: t('bf_cat_fit'), color: '#10B981' };
    if (bf < 25) return { label: t('bf_cat_acceptable'), color: '#F59E0B' };
    return { label: t('bf_cat_high'), color: '#EF4444' };
  }
  if (bf < 13) return { label: t('bf_cat_essential'), color: '#3B82F6' };
  if (bf < 21) return { label: t('bf_cat_athletic'), color: '#10B981' };
  if (bf < 25) return { label: t('bf_cat_fit'), color: '#10B981' };
  if (bf < 32) return { label: t('bf_cat_acceptable'), color: '#F59E0B' };
  return { label: t('bf_cat_high'), color: '#EF4444' };
};

// Methode US Navy (mesures en cm)
const BodyFatCalculator = () => {
  const { t } = useLanguage();
  const [sex, setSex] = useState('');
  const [height, setHeight] = useState('');
  const [neck, setNeck] = useState('');
  const [waist, setWaist] = useState('');
  const [hip, setHip] = useState('');

  const h = parseFloat(height);
  const n = parseFloat(neck);
  const w = parseFloat(waist);
  const hp = parseFloat(hip);

  const valid = sex === 'homme'
    ? h > 0 && n > 0 && w > 0 && w > n
    : h > 0 && n > 0 && w > 0 && hp > 0 && (w + hp) > n;

  let bf = null;
  if (valid) {
    if (sex === 'homme') {
      bf = 495 / (1.0324 - 0.19077 * Math.log10(w - n) + 0.15456 * Math.log10(h)) - 450;
    } else {
      bf = 495 / (1.29579 - 0.35004 * Math.log10(w + hp - n) + 0.221 * Math.log10(h)) - 450;
    }
  }
  const bfRounded = bf !== null && bf > 0 && bf < 70 ? bf : null;
  const cat = bfRounded !== null ? classify(sex, bfRounded, t) : null;

  return (
    <ToolPageLayout
      title={t('bf_title')}
      subtitle={t('bf_subtitle')}
      specialty="generaliste"
    >
      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('cvrisk_sex_label')}</Label>
          <select
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
          >
            <option value="">{t('cvrisk_sex_placeholder')}</option>
            <option value="homme">{t('cvrisk_sex_male')}</option>
            <option value="femme">{t('cvrisk_sex_female')}</option>
          </select>
        </div>
        <div>
          <Label className="mb-1 block">{t('bmi_height_label')}</Label>
          <Input type="number" min="100" value={height} onChange={(e) => setHeight(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('bf_neck_label')}</Label>
          <Input type="number" min="10" value={neck} onChange={(e) => setNeck(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('bf_waist_label')}</Label>
          <Input type="number" min="30" value={waist} onChange={(e) => setWaist(e.target.value)} className="rounded-xl" />
        </div>
        {sex === 'femme' && (
          <div>
            <Label className="mb-1 block">{t('bf_hip_label')}</Label>
            <Input type="number" min="30" value={hip} onChange={(e) => setHip(e.target.value)} className="rounded-xl" />
          </div>
        )}
      </div>

      {cat && (
        <div
          className="rounded-2xl p-6 text-center border"
          style={{ borderColor: `${cat.color}40`, backgroundColor: `${cat.color}0D` }}
        >
          <div className="text-sm text-[#64748B] mb-1">{t('bf_result_label')}</div>
          <div className="text-4xl font-bold" style={{ color: cat.color }}>{bfRounded.toFixed(1)}%</div>
          <div className="mt-2 font-semibold" style={{ color: cat.color }}>{cat.label}</div>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        <Percent className="w-4 h-4 inline text-[#0596DE] mr-1" />
        {t('bf_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default BodyFatCalculator;
